import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ReactElement, ReactNode, SyntheticEvent, useRef } from 'react';

import { IDatesValidation } from '~/services/api/clientAllocation';
import smallAvatarIcon from '~/ui/assets/images/smallAvatar.svg';
import PrivateImage from '~/ui/components/common/PrivateImage';
import ErrorTooltip from '~/ui/pages/ActTeam/reusable/ErrorTooltip';
import getTeamMemberDatesErrors from '~/ui/pages/ActTeam/reusable/ClientAllocationForm/shared/helpers/getTeamMemberDatesErrors';

import variables from '~/ui/assets/styles/colors.module.scss';
import styles from './TeamMemberAccordion.module.scss';

interface IProps {
  panelId: string;
  name: string;
  photo?: string;
  countLabel: string;
  expanded: boolean;
  onToggle: (panelId: string) => (event: SyntheticEvent, isExpanded: boolean) => void;
  badge?: ReactNode;
  teamMemberId: number;
  datesValidation?: IDatesValidation;
  children: ReactNode;
}

const TeamMemberAccordion = ({
  panelId,
  name,
  photo,
  teamMemberId,
  countLabel,
  expanded,
  onToggle,
  badge,
  datesValidation,
  children,
}: IProps): ReactElement => {
  const detailsRef = useRef<HTMLDivElement>(null);
  const datesErrors = getTeamMemberDatesErrors(teamMemberId, datesValidation);

  return (
    <Accordion
      className={styles.accordion}
      slotProps={{
        transition: {
          unmountOnExit: true,
          onEntered: () =>
            detailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }),
        },
      }}
      expanded={expanded}
      onChange={onToggle(panelId)}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="panel-content"
        id="panel-header"
      >
        <Grid container>
          <Grid className={styles.accordionSummaryItemName} size={4}>
            <div className={styles.photoContainer}>
              <PrivateImage className={styles.image} src={photo || smallAvatarIcon} alt="avatar" />
              {badge}
            </div>
            <Typography>{name}</Typography>
            {datesValidation?.isValid === false && !!datesErrors?.errorDates?.length && (
              <ErrorTooltip items={datesErrors?.errorDates} color={variables.colorOrange} />
            )}
          </Grid>

          <Grid className={styles.accordionSummaryItemCenter} size={4}>
            <Typography>{countLabel}</Typography>
          </Grid>

          <Grid className={styles.accordionSummaryItemRight} size={4}>
            <Typography>Details</Typography>
          </Grid>
        </Grid>
      </AccordionSummary>

      <AccordionDetails>
        <div ref={detailsRef}>{children}</div>
      </AccordionDetails>
    </Accordion>
  );
};

export default TeamMemberAccordion;
